import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import type { ChecklistDTO } from '../api';
import { t } from './pdfI18n';

/**
 * Build a pdfmake docDef for a checklist. Rendered server-side (PDF viewer
 * via <PdfPreview> + download via /api/pdf). Columns: done-mark / task / assignee,
 * plus a header (band + checklist name) and a footer page number.
 */
export function buildChecklistDoc(bandName: string | undefined, checklist: ChecklistDTO): TDocumentDefinitions {
  const items = checklist.items || [];
  const done = items.filter((it) => it.done).length;
  const rows = items.map((it) => [
    { text: it.done ? '✓' : '', alignment: 'center' as const, color: it.done ? '#22c55e' : '#bbb' },
    { text: it.text || '—', decoration: (it.done ? 'lineThrough' : undefined) as 'lineThrough' | undefined, color: it.done ? '#888' : '#222' },
    { text: it.assigned_to || '', color: '#666' },
  ]);
  return {
    content: [
      { text: (bandName || t('pdf.cl.checklist')).toUpperCase(), alignment: 'center', bold: true, fontSize: 16 },
      { text: checklist.name || t('pdf.cl.name'), alignment: 'center', fontSize: 13, margin: [0, 4, 0, 4] },
      { text: t('pdf.cl.done', { done, total: items.length }), alignment: 'center', fontSize: 10, color: '#666', margin: [0, 0, 0, 12] },
      {
        table: {
          headerRows: 1,
          widths: [26, '*', 130],
          body: [
            [{ text: '', alignment: 'center' }, { text: t('pdf.cl.col.task'), bold: true }, { text: t('pdf.cl.col.assignee'), bold: true }],
            ...rows,
          ],
        },
        layout: {
          hLineColor: () => '#ddd',
          vLineColor: () => '#eee',
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
        margin: [0, 6, 0, 0],
      },
    ],
    defaultStyle: { font: 'Roboto', fontSize: 11 },
    pageSize: 'A4',
    pageMargins: [40, 50, 40, 50],
    // Serializable spec: the server rebuilds the pdfmake (currentPage,
    // pageCount) function and substitutes {page}/{pages} (a closure would be
    // dropped by JSON.stringify).
    footerSpec: {
      text: '{page} / {pages}',
      alignment: 'right',
      margin: [0, 10, 40, 0],
      fontSize: 9,
      color: '#888',
    },
  };
}
